import React, { useCallback, useEffect, useRef } from 'react';
import { StyledTabItem, StyledTabList, StyledTabPanel, StyledTabItemContainer, StyledTabItemInner, StyledIconButton, StyledOuter, StyledListItem } from './index.styles.js';
import { Icon } from '../icon/index.js';
import { useShowScrollButtons } from '../../hooks/use-show-scroll-buttons.js';
import {} from '@oneaudi/unified-web-common';
import { pickA11yProps } from '../../utils/pick-a11y-props.js';
import { pickDataProps } from '../../utils/pick-data-props.js';
import { useArrowKeyTabs } from '../../hooks/use-arrow-navigation.js';
const TabItem = ({ item, id, size, index, showScrollButtons, containerRef }) => {
    const tabRef = useRef(null);
    const tabId = `${id}-tab-btn-${index}`;
    const tabPanelId = `${id}-tab-pnl-${index}`;
    const iconSize = size === 'md' ? 's' : 'xs';
    const tabItemIcon = item.icon && React.createElement(Icon, { name: item.icon, size: iconSize });
    useEffect(() => {
        const container = containerRef.current;
        if (item.active && container?.contains(document.activeElement)) {
            tabRef.current?.focus();
        }
    }, [item.active, containerRef]);
    return (React.createElement(StyledListItem, { key: tabId, role: "presentation" },
        React.createElement(StyledTabItem, { ref: tabRef, active: item.active, size: size, id: tabId, role: "tab", onClick: item.onClick, "aria-selected": item.active, "aria-controls": tabPanelId, disabled: item.disabled, showScrollButtons: showScrollButtons, tabIndex: item.active ? 0 : -1 },
            React.createElement(StyledTabItemInner, { size: size, active: item.active },
                item.iconPosition === 'leading' && tabItemIcon,
                item.label,
                item.iconPosition === 'trailing' && tabItemIcon))));
};
export const Tabs = React.forwardRef(({ items, size, id, defaultScrollable, scrollButtons, onKeyNavigate, className, margin, ...rest }, ref) => {
    const tabListRef = useRef(null);
    const { isScrollable, inactiveArrow } = useShowScrollButtons(tabListRef);
    const showScrollButtons = defaultScrollable || isScrollable;
    const a11yProps = pickA11yProps(rest);
    const dataProps = pickDataProps(rest);
    const activeIndex = items.findIndex((item) => item.active);
    const scroll = useCallback((direction) => {
        const element = tabListRef.current;
        if (!element) {
            return;
        }
        const multiplier = 0.75;
        const scrollLength = element.offsetWidth * multiplier;
        element.scrollBy({
            left: direction === 'left' ? -scrollLength : scrollLength,
            behavior: 'smooth'
        });
    }, []);
    useArrowKeyTabs(tabListRef, items, activeIndex, onKeyNavigate);
    return (React.createElement(StyledOuter, { ...a11yProps, ...dataProps, className: className, ref: ref, margin: margin },
        React.createElement(StyledTabItemContainer, null,
            showScrollButtons && (React.createElement(StyledIconButton, { variant: "tertiary", icon: "back", size: "md", "aria-label": scrollButtons.left.ariaLabel, onClick: () => scroll('left'), disabled: inactiveArrow === 'left' })),
            React.createElement(StyledTabList, { role: "tablist", ref: tabListRef }, items.map((item, i) => (React.createElement(TabItem, { key: i, item: item, id: id, index: i, size: size, showScrollButtons: showScrollButtons, containerRef: tabListRef })))),
            showScrollButtons && (React.createElement(StyledIconButton, { variant: "tertiary", icon: "forward", size: "md", "aria-label": scrollButtons.right.ariaLabel, onClick: () => scroll('right'), disabled: inactiveArrow === 'right' }))),
        items.map((item, i) => {
            const tabId = `${id}-tab-btn-${i}`;
            const tabPanelId = `${id}-tab-pnl-${i}`;
            return (React.createElement(StyledTabPanel, { size: size, key: tabPanelId, active: item.active, role: "tabpanel", id: tabPanelId, "aria-labelledby": tabId, tabIndex: item.active ? 0 : -1 }, item.content));
        })));
});
Tabs.displayName = 'Tabs';
//# sourceMappingURL=index.js.map